import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";
import SilhouettePicker from "@/components/SilhouettePicker";

const GOALS = [
  { v: "Gain muscle", l: "Build muscle", d: "Eat more, lift heavy, get bigger." },
  { v: "Lose weight", l: "Lose fat", d: "Eat less, stay strong, get leaner." },
  { v: "Build muscle, lose fat", l: "Recomp", d: "Build muscle and lose fat at once." },
  { v: "Stay fit", l: "Maintain", d: "Keep what you have, stay sharp." },
];
const DAYS = [2, 3, 4, 5];
const ACTIVITIES = ["Light", "Moderate", "Heavy", "Athlete"];

function estimateMinsForDays(days) {
  const sets = { 2: 24, 3: 24, 4: 15, 5: 15 }[days] || 20;
  return Math.round(8 + sets * 2.6);
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1 personal fields — pre-fill from user (email/password users) or blank (Google users)
  const [name, setName] = useState(user?.name || "");
  const [age, setAge] = useState(user?.age ?? "");
  const [gender, setGender] = useState(user?.gender || "");
  const [heightCm, setHeightCm] = useState(user?.heightCm ?? "");
  const [weightKg, setWeightKg] = useState(user?.weightKg ?? "");
  const [unitsPref, setUnitsPref] = useState(user?.unitsPref || "metric");
  const [personalErr, setPersonalErr] = useState("");

  const [goal, setGoal] = useState(user?.goal || null);
  const [days, setDays] = useState(user?.daysPerWeek || null);
  const [activity, setActivity] = useState(user?.activity || null);
  const [bodyFatPct, setBodyFatPct] = useState(user?.bodyFatPct ?? null);

  // Step 4 body-fat method
  const [bfMode, setBfMode] = useState("silhouette"); // silhouette | tape
  const [tape, setTape] = useState({ neck: "", waist: "", hip: "" });
  const [tapeErr, setTapeErr] = useState("");

  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const personalValid = useMemo(() => {
    return name.trim().length > 0
      && Number(age) >= 18
      && (gender === "Male" || gender === "Female")
      && Number(heightCm) > 0;
  }, [name, age, gender, heightCm]);

  const canNext =
    (step === 1 && personalValid) ||
    (step === 2 && !!goal) ||
    (step === 3 && !!days) ||
    step === 4;

  const validatePersonal = () => {
    if (!name.trim()) return "Name is required.";
    if (!age || Number(age) < 18) return "You must be 18 or older.";
    if (gender !== "Male" && gender !== "Female") return "Pick a gender.";
    if (!heightCm || Number(heightCm) <= 0) return "Height is required.";
    return "";
  };

  const nextFromPersonal = () => {
    const err = validatePersonal();
    setPersonalErr(err);
    if (err) return;
    setStep(2);
  };

  const computeTapeBF = async () => {
    setTapeErr("");
    try {
      const { data } = await api.post("/measure/navy", {
        gender,
        heightCm: Number(heightCm),
        neckCm: Number(tape.neck),
        waistCm: Number(tape.waist),
        hipCm: gender === "Female" ? Number(tape.hip) : null,
      });
      setBodyFatPct(data.bodyFatPct);
      toast.success(`Body fat: ${data.bodyFatPct}%`);
    } catch (e) {
      setTapeErr(formatApiError(e.response?.data?.detail) || "Invalid measurements");
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        goal, daysPerWeek: days,
        activity, bodyFatPct,
        name: name.trim(),
        age: Number(age),
        gender,
        heightCm: Number(heightCm),
        weightKg: weightKg ? Number(weightKg) : null,
        unitsPref,
      };
      await api.post("/onboarding", payload);
      // If tape method, also log as tape source so it becomes authoritative later
      if (bfMode === "tape" && bodyFatPct != null) {
        try { await api.post("/log/bodyfat", { bodyFatPct, source: "tape" }); }
        catch (e) { console.warn("[onboarding] bodyfat tape log failed", e); }
      } else if (bfMode === "silhouette" && bodyFatPct != null) {
        try { await api.post("/log/bodyfat", { bodyFatPct, source: "silhouette" }); }
        catch (e) { console.warn("[onboarding] bodyfat silhouette log failed", e); }
      }
      await refreshUser();
      toast.success("Program ready.");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-6 pt-14 pb-16">
      <div className="overline">Setup · 0{step}/04</div>
      <h1 className="font-display text-3xl uppercase mt-3">
        {step === 1 && "Basics."}
        {step === 2 && "Pick a goal."}
        {step === 3 && "How often?"}
        {step === 4 && "Two more (optional)."}
      </h1>

      <div className="mt-8">
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-t2 text-sm">Only these three are required: age (18+), gender, height. Weight can wait.</p>

            <div>
              <label className="overline">Name</label>
              <input className="input mt-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="onb-p-name" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline">Age</label>
                <input className="input mt-2" type="number" inputMode="numeric" min={18} value={age}
                  onChange={(e) => setAge(e.target.value)} placeholder="18+" data-testid="onb-p-age" />
              </div>
              <div>
                <label className="overline">Gender</label>
                <div className="flex gap-2 mt-2">
                  {["Male", "Female"].map((g) => (
                    <button key={g} type="button" onClick={() => setGender(g)}
                      className={`chip flex-1 ${gender === g ? "selected" : ""}`}
                      data-testid={`onb-p-gender-${g}`}>{g}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline">Height ({unitsPref === "metric" ? "cm" : "in"})</label>
                <input className="input mt-2" type="number" inputMode="decimal" step="0.1" value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)} placeholder={unitsPref === "metric" ? "e.g. 178" : "e.g. 70"} data-testid="onb-p-height" />
              </div>
              <div>
                <label className="overline">Weight ({unitsPref === "metric" ? "kg" : "lb"})</label>
                <input className="input mt-2" type="number" inputMode="decimal" step="0.1" value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)} placeholder="Optional" data-testid="onb-p-weight" />
              </div>
            </div>

            <div>
              <label className="overline">Units</label>
              <div className="flex gap-2 mt-2">
                {[{v:"metric",l:"Metric"},{v:"imperial",l:"Imperial"}].map((o) => (
                  <button key={o.v} type="button" onClick={() => setUnitsPref(o.v)}
                    className={`chip flex-1 ${unitsPref === o.v ? "selected" : ""}`}
                    data-testid={`onb-p-units-${o.v}`}>{o.l}</button>
                ))}
              </div>
            </div>

            {personalErr && <div className="text-red text-sm" data-testid="onb-p-error">{personalErr}</div>}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 gap-3" data-testid="onb-goal-group">
            {GOALS.map((g) => (
              <button key={g.v} onClick={() => setGoal(g.v)}
                className={`card p-4 text-left transition-colors ${goal === g.v ? "border-lime" : ""} ${!goal ? "pulse-slow" : ""}`}
                data-testid={`onb-goal-${g.v}`}>
                <div className="font-display text-xl uppercase">{g.l}</div>
                <div className="text-sm text-t2 mt-1">{g.d}</div>
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3" data-testid="onb-days-group">
              {DAYS.map((d) => (
                <button key={d} onClick={() => setDays(d)}
                  className={`card py-6 text-center transition-colors ${days === d ? "border-lime" : ""} ${!days ? "pulse-slow" : ""}`}
                  data-testid={`onb-days-${d}`}>
                  <div className="font-display text-3xl">{d}</div>
                  <div className="overline mt-1">days</div>
                </button>
              ))}
            </div>
            {days && (
              <div className="card p-4 text-sm">
                <div className="overline">Split shape</div>
                <div className="mt-2 text-t1">{splitLabel(days)}</div>
                <div className="mt-2 text-t2">~{estimateMinsForDays(days)} min / session</div>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div>
              <div className="overline mb-3">Activity level (optional)</div>
              <div className="grid grid-cols-2 gap-2">
                {ACTIVITIES.map((a) => (
                  <button key={a} onClick={() => setActivity(a)}
                    className={`chip ${activity === a ? "selected" : ""}`}
                    data-testid={`onb-activity-${a}`}>{a}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="overline mb-3">Body fat (optional)</div>
              <div className="flex gap-2 mb-3">
                <button className={`chip flex-1 ${bfMode === "silhouette" ? "selected" : ""}`}
                  onClick={() => setBfMode("silhouette")} data-testid="onb-bf-mode-silhouette">Silhouette</button>
                <button className={`chip flex-1 ${bfMode === "tape" ? "selected" : ""}`}
                  onClick={() => setBfMode("tape")} data-testid="onb-bf-mode-tape">US Navy tape</button>
              </div>
              {bfMode === "silhouette" ? (
                <SilhouettePicker gender={gender || "Male"} value={bodyFatPct} onChange={setBodyFatPct} />
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input className="input" placeholder="Neck (cm)" type="number" step="0.1"
                      value={tape.neck} onChange={(e) => setTape({ ...tape, neck: e.target.value })} data-testid="onb-tape-neck" />
                    <input className="input" placeholder="Waist (cm)" type="number" step="0.1"
                      value={tape.waist} onChange={(e) => setTape({ ...tape, waist: e.target.value })} data-testid="onb-tape-waist" />
                    {gender === "Female" && (
                      <input className="input col-span-2" placeholder="Hip (cm)" type="number" step="0.1"
                        value={tape.hip} onChange={(e) => setTape({ ...tape, hip: e.target.value })} data-testid="onb-tape-hip" />
                    )}
                  </div>
                  <button className="btn btn-outline w-full" onClick={computeTapeBF} data-testid="onb-tape-compute">
                    Compute body fat
                  </button>
                  {bodyFatPct != null && (
                    <div className="text-sm text-lime tabular font-mono" data-testid="onb-tape-result">
                      Body fat: {bodyFatPct}%
                    </div>
                  )}
                  {tapeErr && <div className="text-red text-sm" data-testid="onb-tape-error">{tapeErr}</div>}
                  <div className="text-xs text-t3">Tape is more accurate than silhouette. Measure at the widest waist point and just below the Adam's apple for the neck.</div>
                </div>
              )}
            </div>

            <p className="text-xs text-t3">Fill both now and your macros unlock on day one. Skip for later — your program still starts.</p>
          </div>
        )}
      </div>

      <div className="mt-10 flex gap-3">
        {step > 1 && <button className="btn btn-outline flex-1" onClick={() => setStep(step - 1)} data-testid="onb-back">Back</button>}
        {step === 1 && <button className="btn btn-primary flex-1" onClick={nextFromPersonal} data-testid="onb-next">Continue</button>}
        {step > 1 && step < 4 && <button className="btn btn-primary flex-1" onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="onb-next">Continue</button>}
        {step === 4 && <button className="btn btn-primary flex-1" onClick={submit} disabled={saving || !goal || !days} data-testid="onb-finish">{saving ? "…" : "Start training"}</button>}
      </div>
    </div>
  );
}

function splitLabel(days) {
  return { 2: "Full A · Full B", 3: "Push · Pull · Legs", 4: "Upper · Lower · Upper · Lower", 5: "Push · Pull · Legs · Upper · Lower" }[days];
}
